// --- Sanity ---
const SANITY = { projectId: 'hay4s77t', dataset: 'production', apiVersion: 'v2025-01-01' };
const PAGE_SIZE = 5;
// Reviews dated in the future stay hidden until that date (UTC). Reviews without a date stay visible.
const PUBLISHED = '(!defined(createdAt) || createdAt <= now())';
const REVIEW_GROQ = `{
  _id, originalLanguage, title, body, rating, createdAt,
  translations[]{ language, title, body, replyBody },
  authorName, gender, age, activity, workoutFrequency, flavors, countryCode,
  media[]{
    _type,
    _type == "reviewImage" => { "type": "image", "url": image.asset->url },
    _type == "reviewVideo" => { "type": "video", "url": video.asset->url, "thumb": thumbnail.asset->url }
  },
  brandReply{ brandReplyDate, brandReplyBody }
}`;



const STAR = {
    full: '<svg class="w-[15px]" xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 14 13" fill="none" stroke-width="1"><path d="M2.6775 13L3.815 8.19342L0 4.96053L5.04 4.53289L7 0L8.96 4.53289L14 4.96053L10.185 8.19342L11.3225 13L7 10.4513L2.6775 13Z" fill="currentColor" stroke-width="1"></path></svg>',
    half: '<svg class="w-[15px]" viewBox="0 0 14 13" fill="currentColor"><path d="M5.04199 4.53125L0.00195312 4.95898L3.81738 8.19141L2.67969 12.998L7.00098 10.4492V0Z"/><path class="opacity-45" d="M8.96094 4.5332L14.001 4.96094L10.1855 8.19336L11.3232 13L7.00098 10.4512V0Z"/></svg>',
    empty: '<svg class="w-[15px] opacity-40" viewBox="0 0 14 13" fill="currentColor"><path d="M2.6775 13L3.815 8.19342L0 4.96053L5.04 4.53289L7 0L8.96 4.53289L14 4.96053L10.185 8.19342L11.3225 13L7 10.4513L2.6775 13Z"/></svg>'
};
const VIDEO_PLAY_SVG = '<svg class="w-7" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" fill="none"><circle cx="13" cy="13" r="13" fill="#FCFCFC"></circle><path d="M18 12.5L9.75 17.2631L9.75 7.73686L18 12.5Z" fill="#292929"></path></svg>';

// Map filter menu handles (Liquid handleize) to Sanity flavor values
const FLAVOR_HANDLE_TO_VALUE = {
    'citrus': 'Citrus',
    'forest-fruit': 'Forest Fruit',
    'peach': 'Peach'
};



class ReviewUi extends HTMLElement {
    constructor() {
        super();
        this.selectedRating = null;
        this.selectedSort = null;
        this.selectedFlavor = null;
        this.selectedCountry = null;
        this.selectedMedia = null;
        this.selectedComments = null;
        this.currentPage = 1;
        this.totalCount = 0;
        this._onFiltersChange = this._onFiltersChange.bind(this);
        this._onPaginationClick = this._onPaginationClick.bind(this);
        this._onClick = this._onClick.bind(this);
    }

    async connectedCallback() {
        this.list = this.querySelector('[data-review-list]');
        this.paginationContainer = this.querySelector('[data-review-pagination]');
        this.noReviewsEl = this.querySelector('[data-no-reviews]');
        document.addEventListener('filters-change', this._onFiltersChange);
        if (this.paginationContainer) this.paginationContainer.addEventListener('click', this._onPaginationClick);
        this.addEventListener('click', this._onClick);
        await this.load();
    }

    disconnectedCallback() {
        document.removeEventListener('filters-change', this._onFiltersChange);
        if (this.paginationContainer) this.paginationContainer.removeEventListener('click', this._onPaginationClick);
        this.removeEventListener('click', this._onClick);
    }

    _onClick(e) {
        if (e.target.closest('[data-reset-filters]')) {
            e.preventDefault();
            document.dispatchEvent(new CustomEvent('filters-reset', { bubbles: true }));
        }
    }

    _onFiltersChange(e) {
        const f = e.detail.filters ?? {};
        const ratingVal = f.ratings;
        this.selectedRating = (ratingVal != null && ratingVal !== 'all') ? Number(ratingVal) : null;
        this.selectedSort = f.sort ?? 'most-recent';
        const flavor = f.flavors ?? null;
        this.selectedFlavor = (flavor && flavor !== 'all') ? flavor : null;
        const country = f.countries ?? null;
        this.selectedCountry = (country && country !== 'all') ? country : null;
        this.selectedMedia = f.media === true;
        this.selectedComments = f.comments === true;
        this.currentPage = 1;
        this.load();
    }

    _onPaginationClick(e) {
        const target = e.target.closest('[data-page], [data-prev], [data-next]');
        if (!target || target.disabled) return;
        if (target.hasAttribute('data-prev')) {
            this.currentPage = Math.max(1, this.currentPage - 1);
        } else if (target.hasAttribute('data-next')) {
            const totalPages = Math.ceil(this.totalCount / PAGE_SIZE) || 1;
            this.currentPage = Math.min(totalPages, this.currentPage + 1);
        } else {
            const page = Number(target.getAttribute('data-page'));
            if (Number.isInteger(page) && page >= 1) this.currentPage = page;
        }
        // Touch screens: a short pause between the tap and the scroll up, so the tap (and the arrow's nudge) can be seen first
        const pause = window.matchMedia('(hover: none)').matches ? 250 : 0;
        const tappedAt = Date.now();
        this.load().then(() => {
            setTimeout(() => this.scrollToReviews(), Math.max(0, pause - (Date.now() - tappedAt)));
        });
    }

    scrollToReviews() {
        const filters = this.querySelector('filter-controls');
        if (!filters) return;
        const header = document.querySelector('.shopify-section-header') || document.querySelector('[id*="shopify-section-header"]');
        const headerHeight = header ? header.offsetHeight : 0;
        // Mobile: filters directly below the header. Desktop: same space below the header as between the filters and the review list
        let gap = 0;
        if (window.matchMedia('(min-width: 64rem)').matches) {
            if (this.list) gap = Math.max(0, this.list.getBoundingClientRect().top - filters.getBoundingClientRect().bottom);
        } else {
            // Hide the filters' top border behind the header so only the header's bottom line shows
            gap = -(parseFloat(getComputedStyle(filters).borderTopWidth) || 0);
        }
        const targetTop = filters.getBoundingClientRect().top + window.scrollY - headerHeight - gap;
        if (window.matchMedia('(hover: none)').matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.glideScrollTo(Math.max(0, targetTop));
            return;
        }
        window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    }

    // Touch screens: the browser's own smooth scroll is very fast there, so glide up a little calmer
    // (ease-in-out, 700ms). A touch of the screen stops it.
    glideScrollTo(top) {
        const start = window.scrollY;
        const distance = top - start;
        if (Math.abs(distance) < 2) return;
        const duration = 700;
        const startTime = performance.now();
        let stopped = false;
        const stop = () => { stopped = true; };
        window.addEventListener('touchstart', stop, { once: true, passive: true });
        const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
        const step = (now) => {
            if (stopped) return;
            const t = Math.min(1, (now - startTime) / duration);
            window.scrollTo({ top: start + distance * ease(t), behavior: 'instant' });
            if (t < 1) requestAnimationFrame(step);
            else window.removeEventListener('touchstart', stop);
        };
        requestAnimationFrame(step);
    }


    renderMedia(review, root) {
        const container = root.querySelector('[data-media]');
        if (!container) return;
        const media = Array.isArray(review?.media) ? review.media : [];
        if (!media.length) { container.remove(); return; }
        const fragment = document.createDocumentFragment();
        for (const item of media) {
            if (!item?.url || !item?.type) continue;
            const li = document.createElement('li');
            li.setAttribute('data-media-item', '');
            li.dataset.mediaType = item.type;
            li.dataset.mediaSrc = item.url;
            li.dataset.mediaThumb = item.thumb || '';
            li.className = 'relative w-25 aspect-square cursor-pointer';
            const img = document.createElement('img');
            img.loading = 'lazy';
            img.decoding = 'async';
            img.alt = '';
            img.className = 'inline-block size-full max-w-full object-cover';
            img.src = item.type === 'video' ? (item.thumb || item.url) : item.url;
            if (!img.src) continue;
            li.appendChild(img);
            if (item.type === 'video') {
                const overlay = document.createElement('div');
                overlay.className = 'flex absolute inset-0 justify-center items-center';
                overlay.innerHTML = VIDEO_PLAY_SVG;
                li.appendChild(overlay);
            }
            fragment.appendChild(li);
        }
        if (!fragment.childNodes.length) { container.remove(); return; }
        container.replaceChildren(fragment);
    }


    renderStars(rating, root) {
        const container = root.querySelector('[data-rating]');
        if (!container || typeof rating !== 'number') return;
        const rounded = Math.round(rating * 2) / 2;
        const full = Math.floor(rounded);
        const half = rounded % 1 === 0.5 ? 1 : 0;
        const empty = 5 - full - half;
        container.setAttribute('aria-label', `${rounded} out of 5 stars`);
        container.setAttribute('role', 'img');
        container.innerHTML =
            Array.from({ length: full }, () => STAR.full).join('') +
            Array.from({ length: half }, () => STAR.half).join('') +
            Array.from({ length: empty }, () => STAR.empty).join('');
    }

    renderCountry(code, root) {
        const container = root.querySelector('[data-country-code]');
        if (!container || !code) return;
        const src = typeof window !== 'undefined' && window.roundFlags && window.roundFlags[code];
        if (!src) return;
        const img = document.createElement('img');
        img.className = 'rounded-[100%] size-4 object-cover';
        img.src = src;
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
        img.loading = 'eager';
        img.decoding = 'async';
        img.width = 16;
        img.height = 16;
        container.replaceChildren(img);
        container.setAttribute('aria-label', `Country: ${code}`);
        container.setAttribute('role', 'img');
    }

    setOrRemove(root, selector, value, opts = {}) {
        const el = root.querySelector(selector);
        if (!el) return;
        const removeTarget = opts.closest ? el.closest(opts.closest) : el;
        const empty = value == null || value === '' || (Array.isArray(value) && value.length === 0);
        if (empty) { removeTarget?.remove(); return; }
        el.textContent = String(value);
    }

    getOrderClause() {
        const sort = this.selectedSort ?? 'most-recent';
        if (sort === 'highest-rating') return 'order(rating desc, createdAt desc)';
        if (sort === 'lowest-rating') return 'order(rating asc, createdAt desc)';
        return 'order(createdAt desc)';
    }

    // unfiltered: ignore the filters (keeps the sort), used to show all reviews when the filters give no results
    async fetchReviews(offset, end, { unfiltered = false } = {}) {
        const rating = unfiltered ? null : (this.selectedRating ?? null);
        const flavorHandle = unfiltered ? null : (this.selectedFlavor ?? null);
        const flavorValue = flavorHandle ? (FLAVOR_HANDLE_TO_VALUE[flavorHandle] ?? flavorHandle) : null;
        const country = unfiltered ? null : (this.selectedCountry ?? null);
        const ratingFilter = rating ? `&& rating == ${rating}` : '';
        const flavorFilter = flavorValue ? '&& $flavor in flavors' : '';
        const countryFilter = country ? '&& countryCode == $country' : '';
        const mediaFilter = this.selectedMedia && !unfiltered ? '&& count(media) > 0' : '';
        const replyFilter = this.selectedComments && !unfiltered ? '&& defined(brandReply.brandReplyBody) && length(brandReply.brandReplyBody) > 0' : '';
        const order = this.getOrderClause();
        const filter = `*[_type == "review" && ${PUBLISHED} ${ratingFilter} ${flavorFilter} ${countryFilter} ${mediaFilter} ${replyFilter}]`;
        const query = `{ "total": count(${filter}), "items": ${filter} | ${order} [$offset...$end] ${REVIEW_GROQ} }`;
        let url = `https://${SANITY.projectId}.api.sanity.io/${SANITY.apiVersion}/data/query/${SANITY.dataset}?query=${encodeURIComponent(query)}`;
        url += `&$offset=${offset}&$end=${end}`;
        if (flavorValue) url += `&$flavor=${encodeURIComponent(JSON.stringify(flavorValue))}`;
        if (country) url += `&$country=${encodeURIComponent(JSON.stringify(country))}`;
        const res = await fetch(url);
        const data = await res.json();
        const result = data.result ?? { total: 0, items: [] };
        return { total: result.total ?? 0, items: result.items ?? [] };
    }

    normalizeReview(review, lang) {
        const translation = review.translations?.find(t => t.language === lang);
        const hasTranslation = lang !== review.originalLanguage && !!translation;
        return {
            ...review,
            hasTranslation,
            translation: hasTranslation ? translation : null,
            isTranslated: hasTranslation
        };
    }

    getRelativeDate(dateString) {
        if (!dateString) return '';
        const d = new Date(dateString);
        const today = new Date();
        d.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        const days = Math.floor((today - d) / (1000 * 60 * 60 * 24));
        const s = reviewStrings;
        if (days <= 0) return s.today_label;
        if (days < 14) {
            if (days === 1) return s.one_day_ago_label;
            return s.x_days_ago_label.replace('{{count}}', days);
        }
        if (days < 30) {
            const weeks = Math.floor(days / 7);
            return weeks === 1 ? s.one_week_ago_label : s.x_weeks_ago_label.replace('{{count}}', weeks);
        }
        if (days < 365) {
            const months = Math.floor(days / 30);
            return months === 1 ? s.one_month_ago_label : s.x_months_ago_label.replace('{{count}}', months);
        }
        const years = Math.floor(days / 365);
        return years === 1 ? s.one_year_ago_label : s.x_years_ago_label.replace('{{count}}', years);
    }

    renderReview(review) {
        const template = this.querySelector('template');
        if (!template) return null;
        const clone = template.content.cloneNode(true);
        const strings = window.reviewStrings ?? {};
        const hasMetadata = !!(review.gender || review.age || review.activity?.length || review.workoutFrequency || review.flavors?.length);
        const titleText = (review.isTranslated && review.translation) ? review.translation.title : review.title;
        const bodyText = (review.isTranslated && review.translation) ? review.translation.body : review.body;

        const titleEl = clone.querySelector('[data-title]');
        if (titleEl) titleEl.textContent = titleText;
        const bodyEl = clone.querySelector('[data-body]');
        if (bodyEl) bodyEl.textContent = bodyText;

        this.renderStars(review.rating, clone);
        this.renderCountry(review.countryCode, clone);

        this.setOrRemove(clone, '[data-author-name]', review.authorName);
        this.setOrRemove(clone, '[data-created-at]', review.createdAt ? this.getRelativeDate(review.createdAt) : '');
        this.setOrRemove(clone, '[data-gender]', review.gender ? (strings.gender_options?.[review.gender] ?? review.gender) : '', { closest: 'div' });
        this.setOrRemove(clone, '[data-age]', review.age, { closest: 'div' });
        this.setOrRemove(clone, '[data-activity]', review.activity?.length ? review.activity.map((a) => strings.activity_options?.[a] ?? a).join(', ') : '', { closest: 'div' });
        this.setOrRemove(clone, '[data-workout-frequency]', review.workoutFrequency ? (strings.workout_frequency_options?.[review.workoutFrequency] ?? review.workoutFrequency) : '', { closest: 'div' });

        if (review.flavors?.length) {
            const dd = clone.querySelector('[data-flavors]');
            const dt = clone.querySelector('[data-flavor-label-plural]');
            if (dd) dd.textContent = review.flavors.join(', ');
            if (dt) dt.textContent = review.flavors.length === 1 ? dt.dataset.flavorLabelSingle : dt.dataset.flavorLabelPlural;
        } else {
            clone.querySelector('[data-flavors]')?.closest('div')?.remove();
        }

        this.renderMedia(review, clone);

        const hasReply = !!review.brandReply?.brandReplyBody;
        const contentWrap = clone.querySelector('[data-review-content-wrap]');
        if (contentWrap && hasReply) {
            contentWrap.classList.add('mb-5');

        }
        if (hasReply) {
            const replyText = (review.isTranslated && review.translation?.replyBody) ? review.translation.replyBody : review.brandReply.brandReplyBody;
            const replyEl = clone.querySelector('[data-brand-reply-brand-reply-body]');
            if (replyEl) replyEl.textContent = replyText;
            const dateEl = clone.querySelector('[data-brand-reply-brand-reply-date]');
            if (dateEl && review.brandReply.brandReplyDate) dateEl.textContent = this.getRelativeDate(review.brandReply.brandReplyDate);
        } else {
            clone.querySelector('[data-toggle-brand-reply]')?.remove();
            clone.querySelector('[data-brand-reply]')?.remove();
        }

        if (!hasMetadata) {
            clone.querySelector('[data-toggle-details]')?.remove();
            clone.querySelector('[data-details]')?.remove();
        }
        if (!review.hasTranslation) clone.querySelector('[data-toggle-translation]')?.remove();

        const wrapper = clone.querySelector('review-item');
        if (wrapper) wrapper.reviewData = review;
        return clone;
    }

    getPageNumbers(currentPage, totalPages) {
        if (totalPages <= 1) return [];
        const set = new Set([1, totalPages]);
        if (currentPage > 1 && currentPage < totalPages) set.add(currentPage);
        set.add(currentPage - 1);
        set.add(currentPage + 1);
        const pages = [...set].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
        const out = [];
        for (let i = 0; i < pages.length; i++) {
            if (i > 0 && pages[i] - pages[i - 1] > 1) out.push('…');
            out.push(pages[i]);
        }
        return out;
    }

    renderPagination() {
        if (!this.paginationContainer) return;
        const totalPages = Math.ceil(this.totalCount / PAGE_SIZE) || 1;
        if (totalPages <= 1) {
            this.paginationContainer.hidden = true;
            return;
        }
        this.paginationContainer.hidden = false;
        const strings = window.reviewStrings ?? {};
        const prevLabel = strings.pagination_previous_label ?? 'Previous';
        const nextLabel = strings.pagination_next_label ?? 'Next';

        const prevBtn = this.paginationContainer.querySelector('[data-prev]');
        const nextBtn = this.paginationContainer.querySelector('[data-next]');
        const pagesList = this.paginationContainer.querySelector('[data-pagination-pages]');

        if (prevBtn) {
            prevBtn.disabled = this.currentPage === 1;
            prevBtn.setAttribute('aria-label', prevLabel);
        }
        if (nextBtn) {
            nextBtn.disabled = this.currentPage === totalPages;
            nextBtn.setAttribute('aria-label', nextLabel);
        }

        if (pagesList) {
            const pageNumbers = this.getPageNumbers(this.currentPage, totalPages);
            const fragment = document.createDocumentFragment();
            for (const item of pageNumbers) {
                const li = document.createElement('li');
                if (item === '…') {
                    li.className = 'px-1 size-10 flex items-center justify-center text-gray-500';
                    li.textContent = '…';
                    fragment.appendChild(li);
                    continue;
                }
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.setAttribute('data-page', String(item));
                btn.textContent = String(item);
                btn.className = 'min-w-10 h-10 px-2 text-[14.3px] text-gray-600 hover:text-gray-900 transition-colors ';
                if (item === this.currentPage) btn.classList.add('underline');
                li.appendChild(btn);
                fragment.appendChild(li);
            }
            pagesList.replaceChildren(fragment);
        }
    }

    async load() {
        const offset = (this.currentPage - 1) * PAGE_SIZE;
        const end = offset + PAGE_SIZE;
        let { total, items } = await this.fetchReviews(offset, end);
        // No matches: say so, but still show all reviews underneath (like Timeline)
        const noMatches = total === 0;
        if (noMatches) ({ total, items } = await this.fetchReviews(offset, end, { unfiltered: true }));
        this.totalCount = total;
        const lang = document.documentElement.lang || 'en';
        const reviews = (items ?? []).map((r) => this.normalizeReview(r, lang));
        const template = this.list.querySelector('template');
        [...this.list.children].forEach((child) => {
            if (child !== template) child.remove();
        });
        for (const review of reviews) {
            const node = this.renderReview(review);
            if (node) this.list.appendChild(node);
        }
        const hasResults = this.totalCount > 0;
        if (this.noReviewsEl) this.noReviewsEl.hidden = !noMatches;
        if (this.list) this.list.hidden = !hasResults;
        this.renderPagination();
    }
}

customElements.define('review-ui', ReviewUi);


class ReviewItem extends HTMLElement {
    constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
        this.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
    }

    handleClick(e) {
        const mediaClicked = e.target.closest("[data-media-item]");
        if (mediaClicked && this.contains(mediaClicked)) {
            this.handleMediaClick(mediaClicked);
            return;
        }

        const toggleDetailsBtn = e.target.closest("[data-toggle-details]");
        if (toggleDetailsBtn && this.contains(toggleDetailsBtn)) {
            this.toggleSection({
                button: toggleDetailsBtn,
                content: this.querySelector("[data-details]")
            });
            return;
        }

        const toggleReplyBtn = e.target.closest("[data-toggle-brand-reply]");
        if (toggleReplyBtn && this.contains(toggleReplyBtn)) {
            this.toggleSection({
                button: toggleReplyBtn,
                content: this.querySelector("[data-brand-reply]"),
                fadeOnly: true
            });
            return;
        }

        const toggleTranslationBtn = e.target.closest("[data-toggle-translation]");
        if (toggleTranslationBtn && this.contains(toggleTranslationBtn)) {
            this.toggleTranslation(toggleTranslationBtn);
            return;
        }
    }

    handleMediaClick(clicked) {
        const nodes = [...this.querySelectorAll("[data-media-item]")];

        const items = nodes.map(node => ({
            type: node.dataset.mediaType,
            url: node.dataset.mediaSrc
        }));

        const index = nodes.indexOf(clicked);
        if (index === -1) return;

        const modal = document.querySelector("media-modal");
        modal?.open(items, index);
    }

    toggleSection({ button, content, fadeOnly = false }) {
        if (!content) return;

        const isOpen = !content.hasAttribute("hidden") && !content._closing;

        if (isOpen) this.slideClose(content, fadeOnly);
        else this.slideOpen(content, fadeOnly);
        button.setAttribute("aria-expanded", String(!isOpen));

        this.toggleButtonText(button, !isOpen);
    }

    // "Show details" folds open and closed (height + fade) instead of appearing at once.
    // "Show reply" (fadeOnly) only fades in and out, without sliding (Maxime).
    // Instant for people who prefer reduced motion.
    canAnimate(el) {
        return 'animate' in el && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // Padding folds along with the height
    openFrame(content, fadeOnly) {
        if (fadeOnly) return { opacity: 1 };
        const style = getComputedStyle(content);
        return { height: `${content.offsetHeight}px`, paddingTop: style.paddingTop, paddingBottom: style.paddingBottom, opacity: 1 };
    }

    foldedFrame(fadeOnly) {
        if (fadeOnly) return { opacity: 0 };
        return { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 };
    }

    slideOpen(content, fadeOnly = false) {
        content._closing = false;
        content.getAnimations().forEach((a) => a.cancel());
        content.hidden = false;
        if (!this.canAnimate(content)) return;
        if (!fadeOnly) content.style.overflow = 'hidden';
        content.animate(
            [this.foldedFrame(fadeOnly), this.openFrame(content, fadeOnly)],
            { duration: fadeOnly ? 100 : 250, easing: 'ease-out' }
        ).onfinish = () => {
            content.style.overflow = '';
        };
    }

    slideClose(content, fadeOnly = false) {
        if (!this.canAnimate(content)) {
            content.hidden = true;
            return;
        }
        const open = this.openFrame(content, fadeOnly);
        content._closing = true;
        content.getAnimations().forEach((a) => a.cancel());
        if (!fadeOnly) content.style.overflow = 'hidden';
        const animation = content.animate(
            [open, this.foldedFrame(fadeOnly)],
            { duration: fadeOnly ? 80 : 200, easing: 'ease-in', fill: 'forwards' }
        );
        animation.onfinish = () => {
            // Opened again in the meantime: leave it open
            if (!content._closing) return;
            content._closing = false;
            content.hidden = true;
            content.style.overflow = '';
            animation.cancel();
        };
    }

    toggleButtonText(button, forceState = null) {
        const target =
            button.querySelector("[data-show-text][data-hide-text]") || button;

        const showText = target.dataset.showText;
        const hideText = target.dataset.hideText;

        if (!showText || !hideText) return;

        const isExpanded =
            forceState !== null
                ? forceState
                : button.getAttribute("aria-expanded") === "true";

        target.textContent = isExpanded ? hideText : showText;
    }

    toggleTranslation(button) {
        const data = this.reviewData;
        if (!data?.hasTranslation) return;

        data.isTranslated = !data.isTranslated;

        const titleEl = this.querySelector('[data-title]');
        const bodyEl = this.querySelector('[data-body]');
        const replyEl = this.querySelector('[data-brand-reply-brand-reply-body]');

        const swap = () => {
            if (data.isTranslated && data.translation) {

                titleEl.textContent = data.translation.title;
                bodyEl.textContent = data.translation.body;

                if (replyEl) {
                    replyEl.textContent =
                        data.translation.replyBody ||
                        data.brandReply?.brandReplyBody ||
                        '';
                }

            } else {

                titleEl.textContent = data.title;
                bodyEl.textContent = data.body;

                if (replyEl) {
                    replyEl.textContent =
                        data.brandReply?.brandReplyBody || '';
                }
            }
        };

        swap();

        const target =
            button.querySelector("[data-show-text][data-hide-text]") || button;

        const showText = target.dataset.showText; // Show original
        const hideText = target.dataset.hideText; // Hide original (of Translate)

        if (data.isTranslated) {
            // We tonen vertaling → knop moet originele tonen
            target.textContent = showText;
        } else {
            // We tonen origineel → knop moet vertaling tonen
            target.textContent = hideText;
        }

    }
}

customElements.define("review-item", ReviewItem);


class FilterControls extends HTMLElement {
    constructor() {
        super();
        this._activeButton = null;
        this._activeMenu = null;
        this._scrollContainer = null;
        this._layer = null;
        this._state = {};
        /** @type {number} Index into visible country option buttons while search is focused (-1 = none). */
        this._countryKeyboardIndex = -1;

        this._onClick = this._onClick.bind(this);
        this._onOutsidePointerDown = this._onOutsidePointerDown.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
        this._onReset = this._onReset.bind(this);
        this._onRatingSelect = this._onRatingSelect.bind(this);
        this._updatePosition = this._updatePosition.bind(this);
    }

    static get DEFAULT_STATE() {
        return { ratings: 'all', flavors: 'all', countries: 'all', sort: 'most-recent', media: false, comments: false };
    }



    filterCountries(query) {
        const menu = this.querySelector('[data-filter-menu="countries"]');
        if (!menu) return;

        const normalizedQuery = query.trim().toLowerCase();

        const options = menu.querySelectorAll('[role="option"]');

        options.forEach((option) => {
            const button = option.querySelector('button[data-value]');
            if (!button) return;

            const value = button.dataset.value;

            if (value === 'all') {
                option.hidden = normalizedQuery.length > 0;
                return;
            }

            const label = button.textContent.trim().toLowerCase();

            option.hidden = normalizedQuery
                ? !label.includes(normalizedQuery)
                : false;
        });

        this._clearCountryKeyboardState(menu);
        this._syncCountrySearchNoResults(menu);
    }

    /**
     * Shows a live status message when the country search query matches no country rows.
     */
    _syncCountrySearchNoResults(menu) {
        if (!menu || menu.dataset.filterMenu !== 'countries') return;
        const el = menu.querySelector('[data-no-result-found]');
        if (!el) return;
        const query = (menu.querySelector('[data-country-search]')?.value ?? '').trim();
        const countryLis = [...menu.querySelectorAll('[role="option"]')].filter((li) => {
            const btn = li.querySelector('button[data-value]');
            return btn && btn.dataset.value !== 'all';
        });
        const anyVisible = countryLis.some((li) => !li.hidden);
        const show = query.length > 0 && !anyVisible;
        el.hidden = !show;
    }

    _visibleCountryOptionButtons(menu) {
        return [...menu.querySelectorAll('[role="option"]')]
            .filter((li) => !li.hidden)
            .map((li) => li.querySelector('button[data-value]'))
            .filter(Boolean);
    }

    _clearCountryKeyboardState(menu) {
        if (!menu || menu.dataset.filterMenu !== 'countries') return;
        this._countryKeyboardIndex = -1;
        menu.querySelectorAll('button[data-keyboard-active]').forEach((b) => b.removeAttribute('data-keyboard-active'));
    }

    _applyCountryKeyboardHighlight(menu, buttons, idx) {
        this._clearCountryKeyboardState(menu);
        this._countryKeyboardIndex = idx;
        if (idx >= 0 && buttons[idx]) buttons[idx].setAttribute('data-keyboard-active', 'true');
    }

    async loadAvailableCountries() {

        const query = encodeURIComponent(`
            array::unique(
                *[_type == "review" && ${PUBLISHED} && defined(countryCode)].countryCode
            )
        `);

        const url = `https://${SANITY.projectId}.api.sanity.io/${SANITY.apiVersion}/data/query/${SANITY.dataset}?query=${query}`;

        try {
            const res = await fetch(url);
            const data = await res.json();

            const countries = new Set(data.result || []);

            const menu = this.querySelector('[data-filter-menu="countries"]');
            if (!menu) return;

            const options = menu.querySelectorAll('[role="option"]');

            options.forEach((option) => {
                const button = option.querySelector('button[data-value]');
                if (!button) return;

                const value = button.dataset.value;

                if (value === 'all') return;

                if (!countries.has(value)) {
                    option.remove();
                }
            });

            this._syncCountrySearchNoResults(menu);

        } catch (err) {
            console.error('Failed loading available countries', err);
        }
    }

    _syncTriggerSelectedState(key) {
        // Only applies to dropdown filters where "all" means unfiltered.
        if (!key || key === 'sort') return;
        const trigger = this.querySelector(`[data-filter-button="${key}"]`);
        if (!trigger) return;
        const value = this._state?.[key] ?? null;
        const isSelected = value != null && value !== 'all';
        trigger.dataset.selected = String(isSelected);
    }

    async connectedCallback() {
        this._scrollContainer = this.querySelector('[data-filter-scroll]');
        this._layer = this.querySelector('[data-filter-layer]');

        this.addEventListener('click', this._onClick);
        document.addEventListener('pointerdown', this._onOutsidePointerDown, true);
        document.addEventListener('filters-reset', this._onReset);
        document.addEventListener('rating-filter-select', this._onRatingSelect);
        window.addEventListener('resize', this._updatePosition);
        window.addEventListener('scroll', this._updatePosition, true);
        this.addEventListener('keydown', this._onKeydown);

        await this.loadAvailableCountries();

        this.addEventListener('input', (e) => {
            const input = e.target.closest('[data-country-search]');
            if (!input) return;

            this.filterCountries(input.value);
        });


        this._initializeState();
    }

    _onKeydown(e) {
        if (e.key === 'Escape') {
            const menu = this._activeMenu;
            if (menu?.dataset.filterMenu === 'countries') {
                const searchInput = menu.querySelector('[data-country-search]');
                if (searchInput?.value?.trim()) {
                    e.preventDefault();
                    searchInput.value = '';
                    this.filterCountries('');
                    searchInput.focus();
                    return;
                }
            }
            const trigger = this._activeButton;
            this._close();
            trigger?.focus();
            return;
        }

        const menu = this._activeMenu;
        if (!menu || menu.dataset.filterMenu !== 'countries') return;

        const searchInput = menu.querySelector('[data-country-search]');
        const buttons = this._visibleCountryOptionButtons(menu);
        if (!buttons.length) return;

        const isFromSearch = searchInput && e.target === searchInput;

        if (isFromSearch && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            const len = buttons.length;
            let idx = this._countryKeyboardIndex;
            if (idx < 0 || idx >= len) {
                idx = e.key === 'ArrowDown' ? 0 : len - 1;
            } else if (e.key === 'ArrowDown') {
                idx = idx < len - 1 ? idx + 1 : 0;
            } else {
                idx = idx > 0 ? idx - 1 : len - 1;
            }
            this._applyCountryKeyboardHighlight(menu, buttons, idx);
            buttons[idx].scrollIntoView({ block: 'nearest' });
            return;
        }

        if (isFromSearch && e.key === 'Enter') {
            const idx = this._countryKeyboardIndex;
            if (idx >= 0 && idx < buttons.length) {
                e.preventDefault();
                this._handleOption(buttons[idx]);
            }
            return;
        }

        const btn = e.target.closest('[role="option"] button[data-value]');
        if (btn && menu.contains(btn) && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            const cur = buttons.indexOf(btn);
            if (cur < 0) return;
            const next =
                e.key === 'ArrowDown'
                    ? cur < buttons.length - 1
                        ? cur + 1
                        : 0
                    : cur > 0
                        ? cur - 1
                        : buttons.length - 1;
            this._clearCountryKeyboardState(menu);
            buttons[next].focus();
            buttons[next].scrollIntoView({ block: 'nearest' });
        }
    }

    disconnectedCallback() {
        document.removeEventListener('pointerdown', this._onOutsidePointerDown, true);
        document.removeEventListener('filters-reset', this._onReset);
        document.removeEventListener('rating-filter-select', this._onRatingSelect);
        window.removeEventListener('resize', this._updatePosition);
        window.removeEventListener('scroll', this._updatePosition, true);
        this.removeEventListener('keydown', this._onKeydown);
    }

    _onReset() {
        this._state = { ...FilterControls.DEFAULT_STATE };
        const menus = this.querySelectorAll('[data-filter-menu]');
        menus.forEach((menu) => {
            const key = menu.dataset.filterMenu;
            const defaultValue = this._state[key];
            const optionButtons = menu.querySelectorAll('[role="option"] button');
            optionButtons.forEach((btn) => {
                const isDefault = (btn.dataset.value ?? null) === (defaultValue ?? 'all');
                btn.setAttribute('aria-selected', String(isDefault));
                if (isDefault) {
                    const trigger = this.querySelector(`[data-filter-button="${key}"]`);
                    if (trigger) {
                        const span = trigger.querySelector('span');
                        const label = span || [...trigger.childNodes].find((n) => n.nodeType === Node.TEXT_NODE);
                        if (label) label.textContent = btn.textContent.trim();
                    }
                }
            });
            this._syncTriggerSelectedState(key);
        });
        const toggles = this.querySelectorAll('button[data-filter-toggle]');
        toggles.forEach((btn) => {
            const key = btn.dataset.filterToggle;
            const targetState = this._state[key] === true;
            btn.setAttribute('aria-pressed', String(targetState));
        });
        this._close();
        this._emitChange();
    }

    // A bar in the rating breakdown was clicked: select that rating in the "All ratings" dropdown
    // (value 'all' clears it), exactly as if the option had been chosen in the dropdown itself.
    _onRatingSelect(e) {
        const value = String(e.detail?.value ?? 'all');
        const menu = this.querySelector('[data-filter-menu="ratings"]');
        const trigger = this.querySelector('[data-filter-button="ratings"]');
        if (!menu || !trigger) return;
        const optionButton = menu.querySelector(`[role="option"] button[data-value="${value}"]`);
        if (!optionButton) return;
        if (this._activeMenu) this._close();
        const span = trigger.querySelector('span');
        if (span) span.textContent = optionButton.textContent.trim();
        menu.querySelectorAll('[role="option"] button').forEach((btn) => btn.setAttribute('aria-selected', String(btn === optionButton)));
        this._state.ratings = value;
        this._syncTriggerSelectedState('ratings');
        this._emitChange();
    }

    _initializeState() {
        this._state = {};

        // Dropdowns
        const menus = this.querySelectorAll('[data-filter-menu]');
        menus.forEach((menu) => {
            const key = menu.dataset.filterMenu;
            const selected = menu.querySelector('[role="option"] button[aria-selected="true"]');
            this._state[key] = selected?.dataset.value ?? null;
            this._syncTriggerSelectedState(key);
        });

        // Toggles
        const toggles = this.querySelectorAll('button[data-filter-toggle]');
        toggles.forEach((button) => {
            const key = button.dataset.filterToggle;
            const isPressed = button.getAttribute('aria-pressed') === 'true';
            this._state[key] = isPressed;
        });
    }

    _onClick(e) {
        const clearSearchBtn = e.target.closest('[data-clear-search]');
        if (clearSearchBtn) return this._handleClearCountrySearch(clearSearchBtn, e);

        const button = e.target.closest('[data-filter-button]');
        const optionButton = e.target.closest('[role="option"] button');
        const toggleButton = e.target.closest('button[data-filter-toggle]');

        if (button) return this._handleTrigger(button, e);
        if (optionButton) return this._handleOption(optionButton);
        if (toggleButton) return this._handleToggle(toggleButton);
    }

    _handleClearCountrySearch(btn, e) {
        e.preventDefault();
        e.stopPropagation();
        const menu = btn.closest('[data-filter-menu="countries"]');
        if (!menu) return;
        const input = menu.querySelector('[data-country-search]');
        if (!input) return;
        input.value = '';
        this.filterCountries('');
        input.focus();
    }

    _handleTrigger(button, e) {
        e.stopPropagation();

        const key = button.dataset.filterButton;
        const menu = this.querySelector(`[data-filter-menu="${key}"]`);
        if (!menu) return;

        const isOpen = this._activeMenu === menu;
        this._close();

        if (!isOpen) this._open(button, menu);
    }

    _handleOption(optionButton) {
        const menu = optionButton.closest('[data-filter-menu]');
        if (!menu) return;

        const key = menu.dataset.filterMenu;
        const trigger = this.querySelector(`[data-filter-button="${key}"]`);
        if (!trigger) return;


        // Update trigger label
        const label = optionButton.textContent.trim();
        const span = trigger.querySelector('span');
        if (span) span.textContent = label;
        else trigger.childNodes[0].textContent = label;

        // Update state with value
        this._state[key] = optionButton.dataset.value ?? null;
        this._syncTriggerSelectedState(key);

        this._emitChange();
        // Mark the new option as selected only once the list has faded out, so the tap shows just the tik-state
        this._close(() => {
            menu.querySelectorAll('[role="option"] button').forEach((btn) => btn.setAttribute('aria-selected', 'false'));
            optionButton.setAttribute('aria-selected', 'true');
        });
    }

    _handleToggle(button) {
        const key = button.dataset.filterToggle;
        const isPressed = button.getAttribute('aria-pressed') === 'true';
        const newState = !isPressed;

        button.setAttribute('aria-pressed', String(newState));
        this._state[key] = newState;

        this._emitChange();
    }

    _onOutsidePointerDown(e) {
        if (!this._activeMenu) return;
        const t = e.target;
        if (this._activeButton?.contains(t) || this._activeMenu.contains(t)) return;
        this._close();
    }

    _open(button, menu) {
        this._close();

        this._activeButton = button;
        this._activeMenu = menu;

        button.setAttribute('aria-expanded', 'true');
        this._finishClose(menu);
        menu.hidden = false;
        menu.classList.add('absolute');

        this._updatePosition();

        // Same fade as the "Compare protein source" dropdown. Uses `translate`, because `transform` holds the position.
        if (this._menuCanAnimate(menu)) {
            menu.animate(
                [{ opacity: 0, translate: '0 -4px' }, { opacity: 1, translate: '0 0' }],
                { duration: 200, easing: 'ease-out' }
            );
        }

        if (menu.dataset.filterMenu === 'countries') {
            this._syncCountrySearchNoResults(menu);
            this._clearCountryKeyboardState(menu);
            const searchInput = menu.querySelector('[data-country-search]');

            if (searchInput) {
                if (window.matchMedia('(pointer: fine)').matches) {
                    searchInput.focus({ preventScroll: false });
                }
            }
        }
    }

    _close(afterHidden) {
        if (!this._activeMenu) return;

        const menu = this._activeMenu;
        this._clearCountryKeyboardState(menu);

        this._activeButton.setAttribute('aria-expanded', 'false');

        this._activeButton = null;
        this._activeMenu = null;

        menu._afterHidden = afterHidden;
        if (!this._menuCanAnimate(menu)) {
            menu.hidden = true;
            this._finishClose(menu);
            return;
        }
        menu.getAnimations().forEach((a) => a.cancel());
        menu.animate(
            [{ opacity: 1, translate: '0 0' }, { opacity: 0, translate: '0 -4px' }],
            { duration: 160, easing: 'ease-in', fill: 'forwards' }
        ).onfinish = () => {
            // Opened again in the meantime: leave it open
            if (this._activeMenu === menu) return;
            menu.hidden = true;
            this._finishClose(menu);
        };
    }

    // Ends a running close fade and runs what had to wait until the list was gone
    _finishClose(menu) {
        menu.getAnimations().forEach((a) => a.cancel());
        const afterHidden = menu._afterHidden;
        menu._afterHidden = null;
        afterHidden?.();
    }

    _menuCanAnimate(menu) {
        return 'animate' in menu && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    _updatePosition() {
        if (!this._activeButton || !this._activeMenu) return;

        const buttonRect = this._activeButton.getBoundingClientRect();
        const rootRect = this.getBoundingClientRect();

        const left = buttonRect.left - rootRect.left;
        const top = buttonRect.bottom - rootRect.top;

        this._activeMenu.style.transform = `translate3d(${left}px, ${top}px, 0)`;
    }

    _emitChange() {
        this.dispatchEvent(
            new CustomEvent('filters-change', {
                bubbles: true,
                detail: { filters: { ...this._state } },
            })
        );
    }
}

customElements.define('filter-controls', FilterControls);


// --- Rating breakdown (left summary card): counts per star from Sanity, bars double as the rating filter ---
class RatingBreakdown extends HTMLElement {
    constructor() {
        super();
        this.selected = null;
        this._onClick = this._onClick.bind(this);
        this._onFiltersChange = this._onFiltersChange.bind(this);
    }

    connectedCallback() {
        this.rows = [...this.querySelectorAll('[data-rating-row]')];
        this.addEventListener('click', this._onClick);
        document.addEventListener('filters-change', this._onFiltersChange);
        this.load();
    }

    disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        document.removeEventListener('filters-change', this._onFiltersChange);
    }

    async load() {
        const counts = await this.fetchCounts();
        if (counts) this.render(counts);
        // Average and 'Based on N' stay hidden until the live Sanity count is in (or failed),
        // so the Shopify metafield value (which can include future-dated reviews) never flashes
        const card = this.closest('[data-sticky-summary]') ?? this.parentElement;
        card?.setAttribute('data-stats-ready', '');
    }

    async fetchCounts() {
        const query = `{ ${[5, 4, 3, 2, 1].map((r) => `"r${r}": count(*[_type == "review" && ${PUBLISHED} && rating == ${r}])`).join(', ')} }`;
        const url = `https://${SANITY.projectId}.api.sanity.io/${SANITY.apiVersion}/data/query/${SANITY.dataset}?query=${encodeURIComponent(query)}`;
        try {
            const res = await fetch(url);
            if (!res.ok) return null;
            const data = await res.json();
            const r = data.result ?? {};
            return { 5: r.r5 ?? 0, 4: r.r4 ?? 0, 3: r.r3 ?? 0, 2: r.r2 ?? 0, 1: r.r1 ?? 0 };
        } catch (e) {
            return null;
        }
    }

    // 10000+ is shortened to "10k" so the count column stays narrow
    static formatCount(n) {
        return n >= 10000 ? `${Math.floor(n / 1000)}k` : String(n);
    }

    // Measures the text itself (not the box), so the widest count never sticks out past the column
    sizeCountColumn() {
        const range = document.createRange();
        const widths = this.rows.map((row) => {
            const el = row.querySelector('[data-rating-count]');
            if (!el || !el.textContent) return 0;
            range.selectNodeContents(el);
            return range.getBoundingClientRect().width;
        });
        const widest = Math.max(0, ...widths);
        if (widest > 0) this.style.setProperty('--rating-count-width', `${Math.ceil(widest)}px`);
    }

    render(counts) {
        const total = [5, 4, 3, 2, 1].reduce((sum, r) => sum + counts[r], 0);
        this.rows.forEach((row) => {
            const rating = Number(row.dataset.ratingRow);
            const n = counts[rating] ?? 0;
            const fill = row.querySelector('[data-rating-fill]');
            const count = row.querySelector('[data-rating-count]');
            // Real proportion of all reviews; a rating that has reviews always shows at least a 6px block
            if (fill) fill.style.width = n > 0 && total > 0 ? `max(6px, ${(n / total) * 100}%)` : '0';
            if (count) count.textContent = RatingBreakdown.formatCount(n);
        });

        // Count column as wide as the widest count, so the gap to the bars is the same in every row
        this.sizeCountColumn();
        if (document.fonts?.ready) document.fonts.ready.then(() => this.sizeCountColumn());

        // Average and total in the summary come from the same numbers, so everything adds up
        if (total > 0) {
            const card = this.parentElement;
            const average = [5, 4, 3, 2, 1].reduce((sum, r) => sum + r * counts[r], 0) / total;
            const averageEl = card?.querySelector('[data-rating-average]');
            if (averageEl) averageEl.textContent = average.toFixed(1);
            const totalEl = card?.querySelector('[data-rating-total-label]');
            const template = this.dataset.countLabel;
            if (totalEl && template) totalEl.textContent = template.replace('[count]', String(total));
        }
    }

    _onClick(e) {
        const row = e.target.closest('[data-rating-row]');
        if (!row) return;
        const rating = row.dataset.ratingRow;
        const value = this.selected === rating ? 'all' : rating;
        document.dispatchEvent(new CustomEvent('rating-filter-select', { detail: { value } }));
    }

    _onFiltersChange(e) {
        const value = e.detail?.filters?.ratings;
        this.selected = value && value !== 'all' ? String(value) : null;
        this.toggleAttribute('data-has-selection', this.selected !== null);
        this.rows.forEach((row) => row.setAttribute('aria-pressed', String(row.dataset.ratingRow === this.selected)));
    }
}

customElements.define('rating-breakdown', RatingBreakdown);

// Sticky summary card (desktop): keep it 20px below the auto-hiding header, following its slide in/out
(function initStickySummary() {
    const card = document.querySelector('[data-sticky-summary]');
    if (!card) return;
    const header = document.querySelector('.shopify-section-header') || document.querySelector('[id*="shopify-section-header"]');
    const GAP = 20;
    let frame = null;
    let followUntil = 0;

    // A modal (e.g. Write a review) puts overflow:hidden on html/body. That turns body into the
    // scroll container, so sticky lets go and the card would jump back up. Hold it in place instead.
    let lastTop = null;
    let frozen = false;
    const root = document.documentElement;
    const syncLock = () => {
        const locked = root.classList.contains('overflow-hidden');
        if (locked && !frozen && lastTop !== null && getComputedStyle(card).position === 'sticky') {
            frozen = true;
            card.style.transform = '';
            card.style.transform = `translateY(${Math.round(lastTop - card.getBoundingClientRect().top)}px)`;
        } else if (!locked && frozen) {
            frozen = false;
            card.style.transform = '';
            follow();
        }
    };
    new MutationObserver(syncLock).observe(root, { attributes: true, attributeFilter: ['class'] });

    // Stop at the bottom of the last review (not below the pagination)
    const list = document.querySelector('[data-review-list]');

    const update = () => {
        if (frozen) { frame = null; return; }
        const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        let top = Math.round(headerBottom) + GAP;
        const listRect = list?.getBoundingClientRect();
        if (listRect && listRect.height > card.offsetHeight) {
            top = Math.min(top, Math.round(listRect.bottom - card.offsetHeight));
        }
        card.style.setProperty('--summary-sticky-top', `${top}px`);
        lastTop = card.getBoundingClientRect().top;
        // The header slides for ~0.6s after scrolling stops, so keep following it a bit longer
        frame = performance.now() < followUntil ? requestAnimationFrame(update) : null;
    };
    const follow = () => {
        followUntil = performance.now() + 800;
        if (!frame) frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', follow, { passive: true });
    window.addEventListener('resize', follow);
    if (list && 'ResizeObserver' in window) new ResizeObserver(follow).observe(list);
    follow();
})();

// "Read reviews" (hero) on desktop: land with 20px of space above the reviews block (summary card + filters)
document.addEventListener('click', (e) => {
    const link = e.target.closest?.('a[href="#reviews"]');
    if (!link || !window.matchMedia('(min-width: 64rem)').matches) return;
    const layout = document.querySelector('#reviews .reviews-layout');
    if (!layout) return;
    e.preventDefault();
    const top = layout.getBoundingClientRect().top + window.scrollY - 20;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'instant' : 'smooth' });
});
